<script lang="ts" setup>
import { IconPosition } from "~/enums/IconPositionEnum";
// Explicit import required: the auto-import name for components/Buttons/
// is ButtonsInternalRedirectButton, so the bare tag resolves to nothing.
import InternalRedirectButton from "~/components/Buttons/InternalRedirectButton.vue";

usePageSeo('seoAccommodationTitle', 'seoAccommodationDescription');

const fekRedirect = "https://www.eooa.gr/el/%CE%BF%CE%BC%CE%BF%CF%83%CF%80%CE%BF%CE%BD%CE%B4%CE%AF%CE%B1/%CE%BA%CE%B1%CF%84%CE%B1%CF%86%CF%8D%CE%B3%CE%B9%CE%B1/%CE%BA%CE%B1%CE%BD%CE%BF%CE%BD%CE%B9%CF%83%CE%BC%CF%8C%CF%82-%CE%BB%CE%B5%CE%B9%CF%84%CE%BF%CF%85%CF%81%CE%B3%CE%AF%CE%B1%CF%82-%CE%BA%CE%B1%CF%84%CE%B1%CF%86%CF%85%CE%B3%CE%AF%CF%89%CE%BD"

const essentials = [
  'sleepingBag',
  'personalSheets',
  'pillowCase',
  'toiletries',
  'flashLight'
]
</script>

<template>
  <div class="accommodation-page">
    <Hero 
      :title="$t('Accommodation')" 
      :subtitle="$t('overnightHeader')"
      backgroundImage="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614490/refuge/inside/commonArea_3_y9cygv.jpg"
    />

    <div class="main-container">
      <!-- Section 1: Sleeping -->
      <section id="sleeping" tabindex="-1" class="editorial-section">
        <div class="section-image">
          <nuxt-img 
            :alt="$t('altBeds')"
            src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614497/refuge/inside/sleepingArea_1_sdpz17.jpg"
            quality="60"
          />
        </div>
        <div class="section-text">
          <span class="section-tag">{{ $t('capacityTag') }}</span>
          <h2>{{ $t('sleepingAccommodation') }}</h2>
          <p class="description">{{ $t('refugeCapacity') }}</p>
          
          <div class="essentials-box">
            <h3>{{ $t('essentialsTag') }}</h3>
            <ul class="essentials-list">
              <li v-for="item in essentials" :key="item">
                <Icon name="ph:check-bold" class="check-icon" />
                {{ $t(item) }}
              </li>
            </ul>
          </div>

          <div class="disclaimer-box">
             <p class="small-text">{{ $t('byLawSleepingUtilitiesNotRequired') }}</p>
             <InternalRedirectButton 
                :text="$t('forMoreInfoCheckFEK')" 
                :redirect="fekRedirect" 
                target="_blank"
                icon="ph:arrow-square-out" 
                :icon-position="IconPosition.Right"
              />
          </div>
        </div>
      </section>

      <!-- Section 2: Camping -->
      <section id="camping" tabindex="-1" class="editorial-section reverse">
        <div class="section-image">
          <nuxt-img 
            :alt="$t('altTentOutside')"
            src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1743292010/refuge/2025/6R2A2328_itiim5.jpg"
            quality="60"
          />
        </div>
        <div class="section-text">
          <span class="section-tag">{{ $t('adventureTag') }}</span>
          <h2>{{ $t('Camping') }}</h2>
          <div class="description-group">
            <p v-for="(paragraph, index) in $t('campingToSplit').split('.').filter(p => p.trim())" :key="index">
              {{ paragraph.trim() }}.
            </p>
          </div>
          <InternalRedirectButton 
            :text="$t('overnightPricing')" 
            redirect="/refuge/booking#pricing"
          />
        </div>
      </section>

      <!-- Section 3: Common Area -->
      <section id="common-area" tabindex="-1" class="editorial-section">
        <div class="section-image">
          <nuxt-img 
            :alt="$t('altCommonArea')"
            src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614490/refuge/inside/commonArea_3_y9cygv.jpg"
            quality="60"
          />
        </div>
        <div class="section-text">
          <span class="section-tag">{{ $t('amenitiesTag') }}</span>
          <h2>{{ $t('Common Area') }}</h2>
          <div class="description-group">
            <p v-for="(paragraph, index) in $t('commonAreaToSplit').split('.').filter(p => p.trim())" :key="index">
              {{ paragraph.trim() }}.
            </p>
          </div>
          <InternalRedirectButton 
            :text="$t('Photos')" 
            redirect="/refuge/photos"
          />
        </div>
      </section>

      <!-- Section 4: Kitchen -->
      <section id="kitchen" tabindex="-1" class="editorial-section reverse">
        <div class="section-image">
          <nuxt-img 
            :alt="$t('altKitchen')"
            src="https://res.cloudinary.com/dlct0dnwt/image/upload/v1684614494/refuge/inside/kitchen_1_dcwvgr.jpg"
            quality="60"
          />
        </div>
        <div class="section-text">
          <span class="section-tag">{{ $t('Food & Drink') }}</span>
          <h2>{{ $t('Kitchen') }}</h2>
          <div class="description-group">
            <p v-for="(paragraph, index) in $t('kitchenToSplit').split('.').filter(p => p.trim())" :key="index">
              {{ paragraph.trim() }}.
            </p>
          </div>
          <InternalRedirectButton 
            :text="$t('Menu')" 
            redirect="/services/menu"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.accommodation-page
  width: 100%

.main-container
  max-width: 1200px
  margin: 0 auto
  padding: 6rem 2rem 10rem

.editorial-section
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 6rem
  align-items: center
  margin-bottom: 10rem
  
  &.reverse
    .section-image
      order: 2
    .section-text
      order: 1

.section-image
  height: 600px
  img
    width: 100%
    height: 100%
    object-fit: cover
    border-radius: $border-radius-card
    box-shadow: $shadow-modern

.section-text
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 1.5rem

.section-tag
  text-transform: uppercase
  letter-spacing: 2px
  font-size: 0.9rem
  font-weight: 800
  color: $color-primary

.description
  font-size: 1.25rem
  line-height: 1.6
  color: $color-text-main
  margin: 0

.dark-mode .description
  color: $color-text-light

.description-group
  display: flex
  flex-direction: column
  gap: 1.25rem
  p
    font-size: 1.15rem
    line-height: 1.7
    opacity: 0.8

h2
  font-size: 3.5rem
  font-weight: 800
  margin: 0
  line-height: 1.1

.essentials-box
  background: rgba(0, 0, 0, 0.03)
  padding: 2rem
  border-radius: 12px
  width: 100%
  margin: 1rem 0

.dark-mode .essentials-box
  background: rgba(255, 255, 255, 0.05)

.essentials-box h3
  margin-top: 0
  margin-bottom: 1.5rem
  font-size: 1rem
  text-transform: uppercase
  letter-spacing: 1px
  opacity: 0.6

.essentials-list
  list-style: none
  padding: 0
  margin: 0
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 1rem

.essentials-list li
  display: flex
  align-items: center
  gap: 0.75rem
  font-size: 1.1rem
  font-weight: 600

.check-icon
  color: $color-primary
  font-size: 1.2rem

.disclaimer-box
  display: flex
  flex-direction: column
  gap: 1.5rem

.small-text
  font-size: 0.95rem
  opacity: 0.7
  font-style: italic

@media (max-width: 1024px)
  .editorial-section
    grid-template-columns: 1fr
    gap: 4rem
    margin-bottom: 8rem
    
    &.reverse
      .section-image
        order: unset
      .section-text
        order: unset
  
  .section-image
    height: 400px
  
  h2
    font-size: 2.5rem

@media (max-width: 640px)
  .essentials-list
    grid-template-columns: 1fr
</style>
